/** There's clearly an error with eslint's parsing of this file and I cannot find it. */
import { FacilitySearchOptionsPicker } from './FacilitySearchOptionsPicker'
import { ProviderSearchOptionsPicker, SPECIALTY_TO_GENDER } from './ProviderSearchOptionsPicker'
import { Box, Typography, Grid, Chip, Collapse, Button } from '@mui/material'
import East from '@mui/icons-material/East'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import doctor_icon from '~/assets/images/doctor.svg'
import location_icon from '~/assets/images/location.svg'
import { logger } from '~/shared/lib/logger'

import { useState, useEffect, useMemo, useRef, useEffectEvent, useCallback } from 'react'
import type { FC } from 'react'
import { DistancePicker } from './DistancePicker'
import { SearchNotAvailable } from './SearchNotAvailable'
import { Person } from '~/shared/types'
import { EditField, InlineLoader, Loader, SectionCard, SegmentedControl } from '~/shared/ui'
import {
  referralSearchPersonAvailability,
  type ReferralSearchPersonAvailabilityCreateResponse,
} from '@fireflyhealth/api'
import { ProviderList } from './ProviderList'
import { useNavigate } from 'react-router'
import { useLocation } from 'react-router'
import qs from 'query-string'
import {
  ClinicalFocusArea,
  SpecialtyDropdownOption,
  SteerageProviderStatus,
  useReferralDetails,
  useReviewSteerageProviders,
  useSteerage,
  useSteerageProviders,
} from '~/entities/steerage'
import {
  useProviderSearch,
  sortingOrderForSuggestedPartners,
  usePlaceOfServices,
  getPlaceOfServicesByCodes,
} from './utils'
import { ProviderDetailWithAdditionalData, ProviderListItem } from './ProviderListItem'
import { queueNotification } from '~/shared/notifications/actions'
import { useDispatch } from 'react-redux'
import {
  ProviderListItemReasonModal,
  ProviderListItemReasonModalHandle,
} from './ProviderListItemReasonModal'
import ProcedureSearchOptionsPicker from './ProcedureSearchOptionsPicker'
import { CPTCode } from './types'
import {
  asArrayOf,
  asFiniteNumber,
  asString,
  isFiniteNumber,
  isRecord,
  isStringValue,
  parseJson,
} from '~/shared/lib/guards'
import { PlaceOfService, useProcedureSuggestionQuery } from './utils'
import ProcedureFacilityList from './ProcedureFacilityList'
import { fireAndForget } from '~/shared/lib/fireAndForget'

const SEARCH_TERM_CHIP_SX = {
  color: 'secondary.main',
  fontWeight: 'bold',
  mr: '4px',
  mb: '4px',
  bgcolor: 'transparent',
}

export type LocationTypeSearchCategory = Pick<SpecialtyDropdownOption, 'id' | 'label'> & {
  type: 'Facility'
  category: 'Facility'
}

/**
 * Reads a JSON-encoded query param and checks it with `parse`. A param that is present but does not
 * match reads as absent and says so in the logs, since the visible result is a search field that
 * silently came up empty.
 *
 * Absent, repeated, and empty all stay quiet: callers build these links with
 * `set('cptCode', value ? JSON.stringify(value) : '')`, so an empty string means "no value here",
 * not malformed JSON.
 */
const useJsonParam = <T,>(
  name: string,
  raw: string | (string | null)[] | null | undefined,
  parse: (value: unknown) => T | null,
): T | null => {
  // Keyed on the string: `qs` hands back a fresh array for a repeated key on every render.
  const value = isStringValue(raw) ? raw : ''
  return useMemo(() => readJsonParam(name, value, parse), [name, value, parse])
}

const readJsonParam = <T,>(
  name: string,
  raw: string | (string | null)[] | null | undefined,
  parse: (value: unknown) => T | null,
): T | null => {
  if (!isStringValue(raw) || raw === '') return null
  const parsed = parse(parseJson(raw))
  if (parsed === null) logger.warn(`[ProviderSearch] Ignoring a malformed ${name} param`)
  return parsed
}

/**
 * These params seed the search form straight from a URL an operator can edit or share, so each one
 * is checked against the shape its consumer reads rather than asserted. A param that does not match
 * reads as absent, which leaves that field of the search empty.
 */
const asLocationType = (value: unknown): LocationTypeSearchCategory | null =>
  isRecord(value) &&
  isFiniteNumber(value.id) &&
  isStringValue(value.label) &&
  value.type === 'Facility' &&
  value.category === 'Facility'
    ? { id: value.id, label: value.label, type: 'Facility', category: 'Facility' }
    : null

/** Stable empties: these feed effect dependency lists, so a fresh [] would re-run them. */
const EMPTY_STRINGS: string[] = []
const EMPTY_NUMBERS: number[] = []

const asStringArray = (value: unknown) => asArrayOf(value, isStringValue)
const asFiniteNumberArray = (value: unknown) => asArrayOf(value, isFiniteNumber)

const asCptCode = (value: unknown): CPTCode | null =>
  isRecord(value) && isFiniteNumber(value.id)
    ? {
        id: value.id,
        code: asString(value.code),
        display: asString(value.display),
        description: asString(value.description),
      }
    : null
export type SpecialtyGroupSearchCategory = Pick<
  SpecialtyDropdownOption,
  'id' | 'label' | 'clinical_focus_areas'
> & {
  type: 'SpecialtyGroup'
  category: 'Specialty'
}

interface ProviderSearchContentProps {
  person: Person
}

// Whether the criteria are enough to search: a name or NPI alone, or a category with a zip code.
const evaluateSearchEnabled = (
  zipCodeValue: string | null | undefined,
  npiValue: string | null,
  providerNameValue: string | null,
  facilityNameValue: string | null,
  locationTypeValue: LocationTypeSearchCategory | null,
  specialtyGroupValue: SpecialtyGroupSearchCategory | null,
  procedureCodeValue: CPTCode | null,
  cptCodeValue: CPTCode | null,
) => {
  const isZipCodeValid = Boolean(zipCodeValue && zipCodeValue.trim().length > 0)

  // Treat empty strings as null for validation purposes
  const hasValidNpi = Boolean(npiValue && npiValue.trim().length > 0)
  const hasValidProviderName = Boolean(providerNameValue && providerNameValue.trim().length > 0)
  const hasValidFacilityName = Boolean(facilityNameValue && facilityNameValue.trim().length > 0)

  // Specific searches (NPI, provider name, facility name) don't require zipcode
  const hasSpecificSearch = hasValidNpi || hasValidProviderName || hasValidFacilityName

  // Location-based searches require zipcode
  const hasGeneralSearch =
    locationTypeValue != null ||
    specialtyGroupValue != null ||
    procedureCodeValue != null ||
    cptCodeValue != null

  return hasSpecificSearch || (isZipCodeValid && hasGeneralSearch)
}

const ProviderSearchContent: FC<ProviderSearchContentProps> = props => {
  const dispatch = useDispatch()
  const maxZipcodeCharLimit = 5

  /**
   * isLoading is alrady checked on the parent component, but this is here
   * for an easier migration
   */
  const { data, isPending: providerSearchIsLoading } = useProviderSearch()
  const {
    isLoading: referralDetailsIsLoading,
    specialtiesAndLocationTypes: originalSpecialtiesAndLocationTypes,
  } = useReferralDetails()
  const isLoading = providerSearchIsLoading || referralDetailsIsLoading

  /*
    Parse the search terms from the URL query params,
    and then use them to seed the initial state of the component.
  */
  const location = useLocation()
  const path = location.pathname
  const urlParams = qs.parse(location.search)

  const specialtyGroupParam = useJsonParam(
    'specialty_group',
    urlParams['specialty_group'],
    asFiniteNumber,
  )
  const locationTypeParam = useJsonParam(
    'location_type',
    urlParams['location_type'],
    asLocationType,
  )

  // Procedure search supports several places of service, so this one is a list.
  const placeOfServiceCodesFromUrl =
    useJsonParam('place_of_service_codes', urlParams['place_of_service_codes'], asStringArray) ??
    EMPTY_STRINGS

  const zipCodeParam = urlParams['zipCode'] ? (urlParams['zipCode'] as string) : null
  const npiParam = urlParams['npi'] ? (urlParams['npi'] as string) : null
  const providerNameParam = urlParams['providerName'] ? (urlParams['providerName'] as string) : null
  const facilityNameParam = urlParams['facilityName'] ? (urlParams['facilityName'] as string) : null
  const procedureCodeParam = useJsonParam('procedureCode', urlParams['procedureCode'], asCptCode)
  const cptCodeParam = useJsonParam('cptCode', urlParams['cptCode'], asString)
  const clinicalFocusAreaUidsParam =
    useJsonParam(
      'clinical_focus_area_uids',
      urlParams['clinical_focus_area_uids'],
      asFiniteNumberArray,
    ) ?? EMPTY_NUMBERS

  const caseDescriptionParam = urlParams['caseDescription']
    ? (urlParams['caseDescription'] as string)
    : null

  const steerageIdParam = urlParams['steerage_id'] ? Number(urlParams['steerage_id']) : null

  const caseIdParam = urlParams['caseId'] ? Number(urlParams['caseId']) : null

  const { data: steerageProviderList = [], isPending: steerageProvidersIsLoading } =
    useSteerageProviders(steerageIdParam)
  const { data: steerage, isPending: steerageIsLoading } = useSteerage(steerageIdParam)

  const searchRadius = urlParams['distance'] ? Number(urlParams['distance']) : 10

  // User entered search parameters
  const [locationType, setLocationType] = useState<LocationTypeSearchCategory | null>(
    locationTypeParam,
  )

  // Hide deprecated options and re-separate SpecialtyGroup and LocationType
  const specialtiesAndLocationTypes = originalSpecialtiesAndLocationTypes.filter(
    option => !option.is_deprecated,
  )
  const specialtyGroups = specialtiesAndLocationTypes.filter(s => s.source === 'SpecialtyGroup')
  const locationTypes = specialtiesAndLocationTypes.filter(s => s.source === 'LocationType')

  // Set the preferred gender based on the specialty if applicable
  let genderParam = urlParams['gender'] ? (urlParams['gender'] as string) : null
  let defaultSpecialtyGroup: SpecialtyGroupSearchCategory | undefined
  const specialty = specialtyGroups.find(s => s.id === specialtyGroupParam)
  if (specialty) {
    defaultSpecialtyGroup = { ...specialty, type: 'SpecialtyGroup', category: 'Specialty' }
    genderParam = SPECIALTY_TO_GENDER[specialty.label] ?? genderParam
  }

  const [specialtyGroup, setSpecialtyGroup] = useState<SpecialtyGroupSearchCategory | null>(
    defaultSpecialtyGroup ?? null,
  )

  const [zipCode, setZipCode] = useState<string | null | undefined>(
    zipCodeParam
      ? zipCodeParam
      : // return first five chars of a zipcode in cases where zipcode of member contains 9 digits
        props.person?.insurance_info?.addresses?.[0]?.zipcode?.substring(0, 5),
  )
  const [npi, setNPI] = useState<string | null>(npiParam)
  const [providerName, setProviderName] = useState<string | null>(providerNameParam)

  const [gender, setGender] = useState<string | null>(genderParam)
  const [facilityName, setFacilityName] = useState<string | null>(facilityNameParam)
  const [procedureCode, setProcedureCode] = useState<CPTCode | null>(procedureCodeParam)
  const [cptCode, setCptCode] = useState<CPTCode | null>(null)

  // Fetch place of service data
  const { data: allPlaceOfServices = [] } = usePlaceOfServices()

  const [placeOfServices, setPlaceOfServices] = useState<PlaceOfService[]>([])

  // The URL's places of service are selected once the list loads, and again when either changes.
  const [urlPlaceOfServicesSource, setUrlPlaceOfServicesSource] = useState<{
    codes: string[]
    options: PlaceOfService[]
  }>()
  const canApplyUrlPlaceOfServices =
    placeOfServiceCodesFromUrl.length > 0 && allPlaceOfServices.length > 0
  const isNewUrlPlaceOfServicesSource =
    urlPlaceOfServicesSource?.codes !== placeOfServiceCodesFromUrl ||
    urlPlaceOfServicesSource?.options !== allPlaceOfServices
  if (canApplyUrlPlaceOfServices && isNewUrlPlaceOfServicesSource) {
    setUrlPlaceOfServicesSource({ codes: placeOfServiceCodesFromUrl, options: allPlaceOfServices })
    setPlaceOfServices(getPlaceOfServicesByCodes(placeOfServiceCodesFromUrl, allPlaceOfServices))
  }

  const [isUserEditingZipCode, setIsUserEditingZipCode] = useState<boolean>(false)
  const [caseDescription] = useState<string | null>(caseDescriptionParam)
  const [steerageId] = useState<number | null>(steerageIdParam)
  const [selectedClinicalFocusAreas, setSelectedClinicalFocusAreas] = useState<ClinicalFocusArea[]>(
    () => {
      if (clinicalFocusAreaUidsParam.length > 0 && defaultSpecialtyGroup?.clinical_focus_areas) {
        return defaultSpecialtyGroup?.clinical_focus_areas?.filter((area: ClinicalFocusArea) =>
          clinicalFocusAreaUidsParam.includes(area.id),
        )
      }
      return []
    },
  )

  const [providerSearchType, setProviderSearchType] = useState<
    'provider' | 'facility' | 'procedure'
  >(cptCodeParam ? 'procedure' : 'provider')

  // Use react-query for CPT code lookup from URL param
  const cptCodeLookupPayload = cptCodeParam
    ? {
        search_query: cptCodeParam,
        limit: 1,
      }
    : null

  // Derive whether clinical focus areas should be included in URL params
  const shouldIncludeClinicalFocusAreasInUrl = selectedClinicalFocusAreas.length > 0

  const { data: cptCodeLookupResults } = useProcedureSuggestionQuery(cptCodeLookupPayload)
  const refreshIsSearchAvailable = async () => {
    setIsSearchAvailableLoading(true)
    try {
      const response = await referralSearchPersonAvailability.create(
        { person_id: props.person?.id ?? 0 },
        {
          data: {
            is_provider_search: providerSearchType == 'provider',
            is_facility_search: providerSearchType == 'facility',
            is_procedure_search: providerSearchType == 'procedure',
            zip_code: zipCode,
          },
        },
      )
      setSearchAvailability(response.data)
      setIsUserEditingZipCode(false)
    } finally {
      // Cleared here so a failed availability check does not leave the search form disabled
      // behind a spinner that never stops.
      setIsSearchAvailableLoading(false)
    }
  }
  const [searchAvailablility, setSearchAvailability] =
    useState<ReferralSearchPersonAvailabilityCreateResponse>()
  const [isSearchAvailableLoading, setIsSearchAvailableLoading] = useState<boolean>(false)

  // The URL's CPT code is selected when its lookup returns.
  const [appliedCptCodeLookup, setAppliedCptCodeLookup] = useState<typeof cptCodeLookupResults>()
  if (cptCodeLookupResults !== appliedCptCodeLookup) {
    setAppliedCptCodeLookup(cptCodeLookupResults)
    const firstCptCode = cptCodeLookupResults?.[0]
    if (firstCptCode) setCptCode(firstCptCode)
  }

  const isSearchEnabled = evaluateSearchEnabled(
    zipCode,
    npi,
    providerName,
    facilityName,
    locationType,
    specialtyGroup,
    procedureCode,
    cptCode,
  )

  // Re-check availability when the user toggles between facility and provider search: for the
  // internal directory, providers may be loaded for a zip while care orgs are not. The zip-code
  // field re-checks on blur rather than per keystroke, so the zip is read here, not depended on.
  const refreshAvailabilityForSearchType = useEffectEvent(() =>
    fireAndForget('[ProviderSearch] refresh availability', refreshIsSearchAvailable),
  )
  useEffect(() => {
    refreshAvailabilityForSearchType()
  }, [providerSearchType])

  const [collapse, setCollapse] = useState<boolean>(false)

  const [distance, setDistance] = useState<number>(searchRadius)
  const [resultCount, setResultCount] = useState<number>(0)
  const [hasMoreResults, setHasMoreResults] = useState<boolean>(false)
  const reportResultCount = useCallback((count: number, hasMore = false) => {
    setResultCount(count)
    setHasMoreResults(hasMore)
  }, [])
  const [hasSearchTerms, setHasSearchTerms] = useState<boolean>(locationType != null)
  const [loadingResults, setLoadingResults] = useState<boolean>(false)
  // Currently this only hides/shows the clinical focus areas, but we can imagine a future where additional search criteria is added
  const [showAllSearchCriteria, setShowAllSearchCriteria] = useState<boolean>(
    shouldIncludeClinicalFocusAreasInUrl,
  )

  const navigate = useNavigate()

  const setSearchTerms = (
    locationType: LocationTypeSearchCategory | null,
    specialtyGroup: SpecialtyGroupSearchCategory | null,
    npi: string | null,
    providerName: string | null,
    gender: string | null,
    facilityName: string | null,
    procedureCode: CPTCode | null,
    cptCode: CPTCode | null,
    clinicalFocusAreas?: ClinicalFocusArea[],
    placeOfServicesParam?: PlaceOfService[],
  ) => {
    if (locationType != null || facilityName != null) {
      setProviderSearchType('facility')
    } else if (
      specialtyGroup != null ||
      npi != null ||
      providerName != null ||
      procedureCode != null
    ) {
      setProviderSearchType('provider')
    } else if (cptCode != null) {
      setProviderSearchType('procedure')
    }

    setNPI(npi)
    setSpecialtyGroup(specialtyGroup)
    setLocationType(locationType)
    setProviderName(providerName)
    setGender(gender)
    setFacilityName(facilityName)
    setProcedureCode(procedureCode)
    setCptCode(cptCode)
    // Always use the provided clinical focus areas or fallback to empty array
    setSelectedClinicalFocusAreas(clinicalFocusAreas ?? [])
    setPlaceOfServices(placeOfServicesParam ?? [])
  }

  let genderLabel: string | null = null
  if (gender === 'M') genderLabel = 'Male Providers'
  if (gender === 'F') genderLabel = 'Female Providers'

  const setSearchRadius = (miles: number) => {
    const queryParams = {
      ...urlParams,
      distance: miles,
    }
    void navigate(`${path}?${qs.stringify(queryParams)}`, { replace: true })

    setDistance(miles)
  }

  const setZip = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Set a flag that disables submission of the form
    // till we check whether search is possible for this
    // zipcode
    setIsUserEditingZipCode(true)
    const zip = event.target?.value
    const queryParams = {
      ...urlParams,
      zipCode: zip,
    }
    void navigate(`${path}?${qs.stringify(queryParams)}`, { replace: true })

    setZipCode(zip)
  }

  const returnToCase = () => {
    // Link back to the cases
    // patients/<user_id>/todos?caseId=<case_id>.
    const basepath = path.substring(0, path.lastIndexOf('/'))

    void navigate(`${basepath}/todos?caseId=${caseIdParam}`, { replace: true })
  }

  let filteredSteerageProviders =
    steerageProviderList?.filter(p => {
      return p.is_system_suggested && p.status === SteerageProviderStatus.UnderReview
    }) ?? []
  const showSystemSuggested = filteredSteerageProviders.length > 0
  filteredSteerageProviders = showSystemSuggested ? filteredSteerageProviders : steerageProviderList

  const providersFromResults: ProviderDetailWithAdditionalData[] = []
  const searchResultsByProviderId: Record<number, ProviderDetailWithAdditionalData> = {}
  filteredSteerageProviders.forEach(p => {
    if (p.search_result) {
      // `search_result` is now typed by the generated client as the search-result
      // row shape that ProviderListItem reads directly.
      const result = p.search_result
      providersFromResults.push(result)
      searchResultsByProviderId[p.id] = result
    }
  })

  // sort the filteredSteerageProviders
  filteredSteerageProviders.slice().sort((a, b) => {
    const orderA = sortingOrderForSuggestedPartners(a)
    const orderB = sortingOrderForSuggestedPartners(b)

    return orderA - orderB
  })

  const { mutateAsync: handleReviewSuggestions, isPending: reviewSuggestionLoading } =
    useReviewSteerageProviders(steerageId, {
      onError: () => {
        dispatch(
          queueNotification({
            variant: 'error',
            message: 'Failed to review the suggestions',
          }),
        )
      },
    })

  const disableDismissAllButton =
    reviewSuggestionLoading || steerageIsLoading || steerageProvidersIsLoading
  const providerReasonRef = useRef<ProviderListItemReasonModalHandle>(null)

  const handleRejectSuggestions = async () => {
    // Get exception reason from the modal if the provider is unknown or not recommended
    let exception: string | null = null
    exception = (await providerReasonRef.current?.chooseReason()) ?? null
    if (exception === null) return
    const ids = filteredSteerageProviders?.map(p => p.id) ?? []
    await handleReviewSuggestions({
      providers: ids,
      approve: false,
      reason_for_rejection: exception,
    })
  }

  // The criteria panel — search-term summary, pickers, and search button —
  // rendered inside a SectionCard below.
  const criteriaContent = (
    <>
      <Box>
        <Grid
          container
          columnSpacing={2}
          sx={{ alignContent: 'space-between', alignItems: 'baseline' }}
        >
          {showSystemSuggested ? (
            <Grid size={8} />
          ) : (
            <Grid sx={{ flexGrow: 1 }}>
              {hasSearchTerms ? (
                <Box>Showing results for:</Box>
              ) : (
                <Box sx={{ mb: 1 }}>Search for:</Box>
              )}
            </Grid>
          )}
        </Grid>
      </Box>
      <Box>
        <Grid
          container
          columnSpacing={2}
          sx={{ alignContent: 'space-between', alignItems: 'flex-end' }}
        >
          {showSystemSuggested || hasSearchTerms ? (
            <>
              <Grid sx={{ flexGrow: 1 }}>
                <Grid container>
                  <Grid>
                    <Chip
                      variant="outlined"
                      size="small"
                      icon={
                        providerSearchType == 'provider' ? (
                          <img src={doctor_icon} alt="Doctor" style={{ marginLeft: '1rem' }} />
                        ) : providerSearchType == 'facility' ? (
                          <img src={location_icon} alt="Location" style={{ marginLeft: '1rem' }} />
                        ) : (
                          <></>
                        )
                      }
                      label={
                        providerSearchType == 'provider'
                          ? 'Providers'
                          : providerSearchType == 'facility'
                            ? 'Facility'
                            : providerSearchType == 'procedure'
                              ? 'Procedure'
                              : ''
                      }
                      sx={SEARCH_TERM_CHIP_SX}
                    />
                  </Grid>

                  {locationType != null && (
                    <Grid>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={locationType.label}
                        sx={SEARCH_TERM_CHIP_SX}
                      />
                    </Grid>
                  )}
                  {specialtyGroup != null && (
                    <Grid>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={specialtyGroup.label}
                        sx={SEARCH_TERM_CHIP_SX}
                      />
                    </Grid>
                  )}
                  {gender != null && (
                    <Grid>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={genderLabel}
                        sx={SEARCH_TERM_CHIP_SX}
                      />
                    </Grid>
                  )}
                  {zipCode && (
                    <Grid>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={zipCode}
                        sx={SEARCH_TERM_CHIP_SX}
                      />
                    </Grid>
                  )}
                  {npi && (
                    <Grid>
                      <Chip size="small" variant="outlined" label={npi} sx={SEARCH_TERM_CHIP_SX} />
                    </Grid>
                  )}
                  {providerName && (
                    <Grid>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={providerName}
                        sx={SEARCH_TERM_CHIP_SX}
                      />
                    </Grid>
                  )}
                  {facilityName && (
                    <Grid>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={facilityName}
                        sx={SEARCH_TERM_CHIP_SX}
                      />
                    </Grid>
                  )}
                  {procedureCode && (
                    <Grid>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={'CPT: ' + procedureCode.code}
                        sx={SEARCH_TERM_CHIP_SX}
                      />
                    </Grid>
                  )}
                  {cptCode && (
                    <Grid>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={cptCode.display}
                        sx={SEARCH_TERM_CHIP_SX}
                      />
                    </Grid>
                  )}
                  {shouldIncludeClinicalFocusAreasInUrl && (
                    <Button
                      onClick={() => {
                        setShowAllSearchCriteria(!showAllSearchCriteria)
                      }}
                      size="small"
                      color="primary"
                      variant="text"
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      sx={{
                        padding: '0 4px 2px 0',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&:focus': {
                          backgroundColor: 'transparent',
                        },
                        '&:active': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      {showAllSearchCriteria ? 'Hide' : `Show`} additional filters
                      {showAllSearchCriteria ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </Button>
                  )}
                  {placeOfServices.length > 0 && (
                    <Grid>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={placeOfServices.map(pos => pos.label).join(', ')}
                        sx={SEARCH_TERM_CHIP_SX}
                      />
                    </Grid>
                  )}
                </Grid>
                <Collapse in={showAllSearchCriteria && shouldIncludeClinicalFocusAreasInUrl}>
                  <Grid container>
                    {selectedClinicalFocusAreas.map(focusArea => (
                      <Grid key={focusArea.id}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={focusArea.label}
                          sx={SEARCH_TERM_CHIP_SX}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Collapse>
              </Grid>
            </>
          ) : null}
          {!showSystemSuggested && !hasSearchTerms ? (
            <Grid size={12}>
              <SegmentedControl
                aria-label="Search type"
                options={[
                  { value: 'provider', label: 'Providers' },
                  { value: 'procedure', label: 'Procedure' },
                  { value: 'facility', label: 'Facility' },
                ]}
                value={providerSearchType}
                onChange={type => {
                  setProviderSearchType(type)
                  // reset the search radius to 10 miles
                  setSearchRadius(10)
                }}
              />
            </Grid>
          ) : null}
          {!showSystemSuggested && !hasSearchTerms ? (
            <>
              <Grid size={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 2 }}>
                  {providerSearchType == 'provider' && (
                    <ProviderSearchOptionsPicker
                      cptCodes={data?.cptCodes ?? []}
                      specialtyGroups={specialtyGroups}
                      onSelect={setSearchTerms}
                      disabled={!searchAvailablility?.is_available}
                      loading={isLoading || loadingResults || isSearchAvailableLoading}
                      selectedSpecialtyGroup={specialtyGroup}
                      selectedNPI={npi}
                      selectedGender={gender}
                      selectedProviderName={providerName}
                      selectedProcedureCode={procedureCode}
                      setSearchRadius={setSearchRadius}
                      selectedClinicalFocusAreas={selectedClinicalFocusAreas}
                    />
                  )}
                  {providerSearchType == 'facility' && (
                    <FacilitySearchOptionsPicker
                      locationTypes={locationTypes}
                      onSelect={setSearchTerms}
                      disabled={!searchAvailablility?.is_available}
                      loading={isLoading || loadingResults || isSearchAvailableLoading}
                      selectedLocationType={locationType}
                      selectedFacilityName={facilityName}
                      setSearchRadius={setSearchRadius}
                    />
                  )}
                  {providerSearchType == 'procedure' && (
                    <ProcedureSearchOptionsPicker
                      locationTypes={locationTypes}
                      onSelect={setSearchTerms}
                      disabled={!searchAvailablility?.is_available}
                      loading={isLoading || loadingResults || isSearchAvailableLoading}
                      selectedLocationType={locationType}
                      selectedFacilityName={facilityName}
                      setSearchRadius={setSearchRadius}
                      selectedCPTCode={cptCode}
                      selectedPlaceOfServices={placeOfServices}
                    />
                  )}
                  <Box sx={{ maxWidth: 640 }}>
                    <EditField
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: maxZipcodeCharLimit },
                      }}
                      label="Zipcode"
                      type="text"
                      name="zip"
                      value={zipCode}
                      onChange={setZip}
                      // Verify that data is available for the zipcode
                      // only when the user finishes typing and moves out of the
                      // zipcode input
                      onBlur={() =>
                        fireAndForget(
                          '[ProviderSearch] refresh search availability',
                          refreshIsSearchAvailable,
                        )
                      }
                      disabled={
                        isLoading ||
                        loadingResults ||
                        isSearchAvailableLoading ||
                        !searchAvailablility?.is_available
                      }
                    />
                  </Box>
                </Box>
                <Box sx={{ pt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                  <Button
                    onClick={() => {
                      const queryParams = {
                        ...urlParams,
                        location_type: JSON.stringify(locationType),
                        // The id, not the object: this param is matched against a specialty id
                        // both here and by the link ReferralDetails builds.
                        specialty_group: specialtyGroup ? JSON.stringify(specialtyGroup.id) : '',
                        npi: npi,
                        providerName: providerName,
                        facilityName: facilityName,
                        cptCode: JSON.stringify(cptCode?.display),
                        ...(shouldIncludeClinicalFocusAreasInUrl && {
                          clinical_focus_area_uids: JSON.stringify(
                            selectedClinicalFocusAreas.map(focusArea => focusArea.id),
                          ),
                        }),
                        ...(placeOfServices.length > 0 && {
                          place_of_service_codes: JSON.stringify(
                            placeOfServices.map(pos => pos.code),
                          ),
                        }),
                      }
                      void navigate(`${path}?${qs.stringify(queryParams)}`, { replace: true })
                      const hasTerms =
                        locationType != null ||
                        specialtyGroup != null ||
                        zipCode != null ||
                        npi != null ||
                        cptCode != null
                      setHasSearchTerms(hasTerms)
                      // Collapse the search header once a search runs so the
                      // results list fills the viewport on small screens.
                      setCollapse(hasTerms)
                    }}
                    disabled={
                      isLoading ||
                      loadingResults ||
                      isSearchAvailableLoading ||
                      !searchAvailablility?.is_available ||
                      !isSearchEnabled ||
                      isUserEditingZipCode
                    }
                    color="primary"
                    variant="contained"
                  >
                    Search
                  </Button>
                </Box>
              </Grid>
            </>
          ) : null}
        </Grid>
        {!(isLoading || loadingResults || isSearchAvailableLoading) &&
          searchAvailablility != null &&
          !searchAvailablility.is_available && (
            <Box sx={{ pt: 2 }}>
              <SearchNotAvailable searchAvailability={searchAvailablility} />{' '}
            </Box>
          )}
      </Box>
    </>
  )

  if (isLoading) return <Loader />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ProviderListItemReasonModal
        ref={providerReasonRef}
        providers={providersFromResults}
        providerSearchType={providerSearchType}
        isSystemSuggested
      />
      {/* Persistent header. The left cluster holds (1) a "Back to case" breadcrumb
          when the screen was deep-linked from a case (?caseId=...), kept on the same
          row so it never stacks into a second breadcrumb line, and (2) the
          state-specific title / "Edit Search" control. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ml: 3,
          mr: 2,
          mt: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {caseIdParam ? (
            <Button
              size="small"
              color="primary"
              variant="text"
              startIcon={<KeyboardArrowLeft />}
              onClick={returnToCase}
              disabled={isLoading || loadingResults || isSearchAvailableLoading}
              title="Back to case"
            >
              Case{caseDescription ? `: ${caseDescription}` : ''}
            </Button>
          ) : null}
          {showSystemSuggested ? (
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              Top Recommendations
              {/* The specialty is already shown by the case breadcrumb, so only
                  append "for {specialty}" when there's no breadcrumb to avoid echoing it. */}
              {specialtyGroup?.label && !caseIdParam ? (
                <Typography variant="body1" component="span">
                  &nbsp;for {specialtyGroup.label}
                </Typography>
              ) : null}
            </Typography>
          ) : hasSearchTerms ? (
            <Button
              size="small"
              color="primary"
              disabled={isLoading || loadingResults || isSearchAvailableLoading}
              startIcon={<KeyboardArrowLeft />}
              onClick={() => {
                setHasSearchTerms(false)
                setCollapse(false)
              }}
            >
              Edit Search
            </Button>
          ) : (
            <span />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Results count + distance picker stay in the persistent header so
              operators can adjust distance without re-expanding the criteria. */}
          {!showSystemSuggested && hasSearchTerms && distance && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mr: 1 }}>
              <Typography sx={{ color: 'secondary.main' }} component="div">
                {isLoading || loadingResults || isSearchAvailableLoading ? (
                  <InlineLoader />
                ) : (
                  `${resultCount}${hasMoreResults ? '+' : ''}`
                )}{' '}
                Results &nbsp;
              </Typography>
              <DistancePicker distance={distance} callback={setSearchRadius} />
            </Box>
          )}
          {hasSearchTerms || showSystemSuggested ? (
            <Button
              size="small"
              color="primary"
              onClick={() => {
                setCollapse(c => !c)
                logger.info('Provider Search: Header collapsed')
              }}
              endIcon={collapse ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
            >
              {collapse ? 'Show' : 'Hide'} search criteria
            </Button>
          ) : null}
        </Box>
      </Box>
      <Collapse in={!collapse}>
        {loadingResults || isSearchAvailableLoading ? <Loader /> : null}
        <Box sx={{ mx: 3, mt: 1, mb: 2 }}>
          <SectionCard>{criteriaContent}</SectionCard>
        </Box>
      </Collapse>
      {showSystemSuggested ? (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {filteredSteerageProviders?.map(p => {
            const provider = searchResultsByProviderId[p.id]
            if (!provider) return null
            return (
              <ProviderListItem
                searchRequestId={null}
                isSystemSuggested
                provider={provider}
                steerageProvider={p}
                steerageID={steerage?.id}
                locationType=""
                specialtyGroupSearchCategory={specialtyGroup}
                providerSearchType={providerSearchType}
                indexInResults={0}
                key={p.id}
                cptCode={p?.cpt_code_object ?? null}
              />
            )
          })}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Button
              variant="outlined"
              endIcon={<East />}
              onClick={() =>
                fireAndForget('[ProviderSearch] reject suggestions', handleRejectSuggestions)
              }
              disabled={disableDismissAllButton}
            >
              Dismiss all suggestion(s) and start a new search
            </Button>
          </Box>
        </Box>
      ) : null}
      {!showSystemSuggested && hasSearchTerms && distance && providerSearchType != 'procedure' && (
        <ProviderList
          person={props.person}
          locationType={locationType}
          specialtyGroup={specialtyGroup}
          clinicalFocusAreas={selectedClinicalFocusAreas}
          distance={distance}
          zipCode={zipCode}
          npi={npi}
          providerName={providerName}
          gender={gender}
          facilityName={facilityName}
          procedureCode={procedureCode}
          resultCountCallback={reportResultCount}
          loadingResultsCallback={setLoadingResults}
          steerageProviderList={steerageProviderList}
          states={data?.states}
          steerageId={steerageId}
          providerSearchType={providerSearchType}
          searchAvailabilityId={searchAvailablility?.id}
        />
      )}
      {!showSystemSuggested && hasSearchTerms && distance && providerSearchType == 'procedure' && (
        <ProcedureFacilityList
          person={props.person}
          distance={distance}
          zipCode={zipCode}
          cptCode={cptCode}
          placeOfServices={placeOfServices}
          resultCountCallback={reportResultCount}
          loadingResultsCallback={setLoadingResults}
          steerageProviderList={steerageProviderList}
          states={data?.states}
          steerageId={steerageId}
          providerSearchType={providerSearchType}
          searchAvailabilityId={searchAvailablility?.id}
        />
      )}
    </Box>
  )
}

export default ProviderSearchContent
